import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { formatMoney } from "@/lib/currency";
import { SkeletonLoader, useToast } from "@/components/system";
import { formatError } from "@/lib/format-error";

type CategoryRow = {
  id: string;
  slug: string;
  display_name: string;
  kind: string;
  monthly_target_amount: number | null;
  monthly_target_currency: string | null;
  archived: boolean;
};

type BudgetActual = {
  category_id: string | null;
  expense_amount: number | null;
  income_amount: number | null;
  net_amount: number | null;
};

/**
 * Seed list. BW12.5 — income side trimmed to the universal "Salary" only;
 * Consulting + Scholarship were over-fitting to a hypothetical user. Users
 * add their own income sources via the "+ Add income source" UI.
 */
const SEED_CATEGORIES = [
  { slug: "rent", display_name: "Rent", kind: "expense", monthly_target_amount: 0 },
  { slug: "utilities", display_name: "Utilities", kind: "expense", monthly_target_amount: 0 },
  { slug: "groceries", display_name: "Groceries", kind: "expense", monthly_target_amount: 0 },
  { slug: "eating_out", display_name: "Eating out", kind: "expense", monthly_target_amount: 0 },
  { slug: "transit", display_name: "Transit", kind: "expense", monthly_target_amount: 0 },
  { slug: "gym", display_name: "Gym & sports", kind: "expense", monthly_target_amount: 0 },
  { slug: "subscriptions", display_name: "Subscriptions", kind: "expense", monthly_target_amount: 0 },
  { slug: "travel", display_name: "Travel", kind: "expense", monthly_target_amount: 0 },
  { slug: "phone", display_name: "Phone & internet", kind: "expense", monthly_target_amount: 0 },
  { slug: "health", display_name: "Health & insurance", kind: "expense", monthly_target_amount: 0 },
  { slug: "personal_care", display_name: "Personal care", kind: "expense", monthly_target_amount: 0 },
  { slug: "household", display_name: "Household", kind: "expense", monthly_target_amount: 0 },
  { slug: "savings", display_name: "Savings", kind: "transfer", monthly_target_amount: 0 },
  { slug: "investments", display_name: "Investments", kind: "investment", monthly_target_amount: 0 },
  { slug: "salary", display_name: "Salary", kind: "income", monthly_target_amount: 0 },
];

export function FinanceBudget() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [addingKind, setAddingKind] = useState<"income" | "expense" | null>(null);
  const [addName, setAddName] = useState("");
  const [addTarget, setAddTarget] = useState("");

  const start = new Date();
  start.setDate(1);
  const monthIso = start.toISOString().slice(0, 10);

  const cats = useQuery({
    queryKey: ["finance_categories", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_category")
        .select("*")
        .eq("user_id", userId!)
        .eq("archived", false)
        .order("kind")
        .order("display_name");
      if (error) throw error;
      return (data ?? []) as CategoryRow[];
    },
  });

  const actuals = useQuery({
    queryKey: ["finance_budget_actuals", userId, monthIso],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_budget_actual_monthly")
        .select("category_id, expense_amount, income_amount, net_amount")
        .eq("user_id", userId!)
        .eq("month", monthIso);
      if (error) throw error;
      return (data ?? []) as BudgetActual[];
    },
  });

  const seed = useMutation({
    mutationFn: async () => {
      setSeeding(true);
      const rows = SEED_CATEGORIES.map((c) => ({ ...c, user_id: userId, monthly_target_currency: "CHF" }));
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_category")
        .upsert(rows, { onConflict: "user_id,slug", ignoreDuplicates: true });
      if (error) throw error;
    },
    onSettled: () => {
      setSeeding(false);
      queryClient.invalidateQueries({ queryKey: ["finance_categories", userId] });
    },
  });

  const updateTarget = useMutation({
    mutationFn: async ({ id, amount }: { id: string; amount: number }) => {
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_category")
        .update({ monthly_target_amount: amount })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["finance_categories", userId] }),
    onError: (e) => toast.show(`Couldn't save target: ${formatError(e)}`),
  });

  // BW12.5 Issue 5: full CRUD on categories.
  const addCategory = useMutation({
    mutationFn: async ({
      kind,
      name,
      target,
    }: {
      kind: "income" | "expense";
      name: string;
      target: number;
    }) => {
      if (!userId) throw new Error("Not signed in");
      const slug = name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "")
        .slice(0, 48);
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_category")
        .insert({
          user_id: userId,
          slug,
          display_name: name.trim(),
          kind,
          monthly_target_amount: target,
          monthly_target_currency: "CHF",
          archived: false,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["finance_categories", userId] });
      toast.show("Category added.");
      setAddingKind(null);
      setAddName("");
      setAddTarget("");
    },
    onError: (e) => toast.show(`Couldn't add: ${formatError(e)}`),
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      // Soft-delete via archived=true so historical transactions stay linked.
      const { error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_category")
        .update({ archived: true })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["finance_categories", userId] });
      toast.show("Category removed.");
    },
    onError: (e) => toast.show(`Couldn't delete: ${formatError(e)}`),
  });

  const actualsByCat = new Map<string, BudgetActual>();
  for (const a of actuals.data ?? []) {
    if (a.category_id) actualsByCat.set(a.category_id, a);
  }

  const expenseCats = (cats.data ?? []).filter((c) => c.kind === "expense");
  const incomeCats = (cats.data ?? []).filter((c) => c.kind === "income");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)" }}>
        <Link to="/finance" style={{ color: "var(--fg-3)", textDecoration: "none" }}>
          <ArrowLeft size={16} style={{ verticalAlign: "middle" }} /> Finance
        </Link>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 28 }}>
          Budget · {start.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </h1>
      </header>

      {(cats.data ?? []).length === 0 && (
        <div
          style={{
            padding: "var(--space-4)",
            border: "1px dashed var(--hair-2)",
            borderRadius: "var(--radius-card)",
            background: "var(--bg-2)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            alignItems: "flex-start",
          }}
        >
          <span>No categories yet. Seed the standard set?</span>
          <button
            onClick={() => seed.mutate()}
            disabled={seeding}
            style={{
              background: "var(--accent)",
              color: "var(--accent-on)",
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Plus size={14} /> Seed 17 categories
          </button>
        </div>
      )}

      {cats.isLoading ? (
        <SkeletonLoader variant="card" height={300} />
      ) : (
        <>
          <Section
            title="Expenses"
            onAdd={() => {
              setAddingKind("expense");
              setAddName("");
              setAddTarget("");
            }}
          >
            {addingKind === "expense" && (
              <AddCategoryRow
                kind="expense"
                name={addName}
                target={addTarget}
                pending={addCategory.isPending}
                onName={setAddName}
                onTarget={setAddTarget}
                onCancel={() => setAddingKind(null)}
                onSubmit={() =>
                  addCategory.mutate({
                    kind: "expense",
                    name: addName.trim(),
                    target: Number(addTarget) || 0,
                  })
                }
              />
            )}
            {expenseCats.map((c) => (
              <BudgetRow
                key={c.id}
                cat={c}
                actual={Math.abs(actualsByCat.get(c.id)?.expense_amount ?? 0)}
                onSave={(n) => updateTarget.mutate({ id: c.id, amount: n })}
                onDelete={() => {
                  if (confirm(`Remove "${c.display_name}"?`)) {
                    deleteCategory.mutate(c.id);
                  }
                }}
                editing={editing === c.id}
                onEdit={() => setEditing(c.id)}
                onCancel={() => setEditing(null)}
              />
            ))}
          </Section>
          <Section
            title="Income"
            onAdd={() => {
              setAddingKind("income");
              setAddName("");
              setAddTarget("");
            }}
          >
            {addingKind === "income" && (
              <AddCategoryRow
                kind="income"
                name={addName}
                target={addTarget}
                pending={addCategory.isPending}
                onName={setAddName}
                onTarget={setAddTarget}
                onCancel={() => setAddingKind(null)}
                onSubmit={() =>
                  addCategory.mutate({
                    kind: "income",
                    name: addName.trim(),
                    target: Number(addTarget) || 0,
                  })
                }
              />
            )}
            {incomeCats.length === 0 && addingKind !== "income" && (
              <div
                style={{
                  padding: "var(--space-4)",
                  color: "var(--fg-3)",
                  fontSize: "var(--type-body-s)",
                }}
              >
                No income sources yet. Tap "+ Add income source" to add one.
              </div>
            )}
            {incomeCats.map((c) => (
              <BudgetRow
                key={c.id}
                cat={c}
                actual={Math.abs(actualsByCat.get(c.id)?.income_amount ?? 0)}
                onSave={(n) => updateTarget.mutate({ id: c.id, amount: n })}
                onDelete={() => {
                  if (confirm(`Remove "${c.display_name}"?`)) {
                    deleteCategory.mutate(c.id);
                  }
                }}
                editing={editing === c.id}
                onEdit={() => setEditing(c.id)}
                onCancel={() => setEditing(null)}
                income
              />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function AddCategoryRow({
  kind,
  name,
  target,
  pending,
  onName,
  onTarget,
  onCancel,
  onSubmit,
}: {
  kind: "income" | "expense";
  name: string;
  target: string;
  pending: boolean;
  onName: (s: string) => void;
  onTarget: (s: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSubmit();
      }}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 140px max-content max-content",
        alignItems: "center",
        gap: "var(--space-2)",
        padding: "12px 16px",
        background: "var(--accent-wash)",
        borderBottom: "1px solid var(--accent-dim)",
      }}
    >
      <input
        autoFocus
        placeholder={kind === "income" ? "e.g. Freelance writing" : "e.g. Pet care"}
        value={name}
        onChange={(e) => onName(e.target.value)}
        style={{
          padding: "6px 10px",
          borderRadius: 6,
          border: "1px solid var(--hair-2)",
          background: "var(--bg)",
          color: "var(--fg)",
          fontSize: 13,
        }}
      />
      <input
        type="number"
        inputMode="decimal"
        placeholder="Target / month"
        value={target}
        onChange={(e) => onTarget(e.target.value)}
        style={{
          padding: "6px 10px",
          borderRadius: 6,
          border: "1px solid var(--hair-2)",
          background: "var(--bg)",
          color: "var(--fg)",
          fontSize: 13,
        }}
      />
      <button
        type="submit"
        disabled={pending || !name.trim()}
        style={{
          background: "var(--accent)",
          color: "#0a0b0d",
          padding: "6px 12px",
          borderRadius: 6,
          border: "none",
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        {pending ? "Saving…" : "Add"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={pending}
        style={{
          background: "transparent",
          color: "var(--fg-3)",
          padding: "6px 12px",
          borderRadius: 6,
          border: "1px solid var(--hair-2)",
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        Cancel
      </button>
    </form>
  );
}

function Section({
  title,
  children,
  onAdd,
}: {
  title: string;
  children: React.ReactNode;
  onAdd?: () => void;
}) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "var(--type-body)", color: "var(--fg-2)" }}>{title}</h2>
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            style={{
              background: "transparent",
              color: "var(--accent)",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontSize: "var(--type-body-s)",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Plus size={12} strokeWidth={1.8} /> Add{" "}
            {title.toLowerCase().replace(/s$/, "")} source
          </button>
        )}
      </header>
      <div
        style={{
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          overflow: "hidden",
          background: "var(--bg-2)",
        }}
      >
        {children}
      </div>
    </section>
  );
}

function BudgetRow({
  cat,
  actual,
  onSave,
  onDelete,
  editing,
  onEdit,
  onCancel,
  income,
}: {
  cat: CategoryRow;
  actual: number;
  onSave: (n: number) => void;
  onDelete: () => void;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  income?: boolean;
}) {
  const target = Number(cat.monthly_target_amount ?? 0);
  const pct = target > 0 ? Math.min(100, (actual / target) * 100) : 0;
  const tone = income
    ? actual >= target && target > 0
      ? "var(--severity-ok)"
      : "var(--severity-warn)"
    : pct < 80
      ? "var(--severity-ok)"
      : pct < 100
        ? "var(--severity-warn)"
        : "var(--severity-bad)";
  const [val, setVal] = useState(target ? String(target) : "");

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 12,
        padding: "12px 16px",
        borderBottom: "1px solid var(--hair)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: "var(--type-body-s)", fontWeight: 500 }}>{cat.display_name}</span>
        <div style={{ height: 6, background: "var(--chip)", borderRadius: 3, overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: tone,
              transition: "width 200ms",
            }}
          />
        </div>
        <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
          {formatMoney(actual, cat.monthly_target_currency ?? "CHF")} of {formatMoney(target, cat.monthly_target_currency ?? "CHF")}
        </span>
      </div>
      {editing ? (
        <div style={{ display: "flex", gap: 6 }}>
          <input
            type="number"
            inputMode="decimal"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="0"
            style={{ width: 100, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--hair-2)", background: "var(--bg)", color: "var(--fg)", fontSize: 13 }}
          />
          <button
            onClick={() => {
              const n = Number(val);
              if (Number.isFinite(n) && n >= 0) {
                onSave(n);
                onCancel();
              }
            }}
            style={{ background: "var(--accent)", color: "var(--accent-on)", padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer" }}
          >
            Save
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={onEdit}
            style={{ background: "transparent", color: "var(--fg-3)", border: "1px solid var(--hair-2)", padding: "6px 10px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}
          >
            Edit target
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label={`Delete ${cat.display_name}`}
            title={`Delete ${cat.display_name}`}
            style={{
              background: "transparent",
              color: "var(--fg-3)",
              border: "1px solid var(--hair-2)",
              padding: "6px 8px",
              borderRadius: 6,
              fontSize: 12,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Trash2 size={12} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
}
